import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Box,
  Typography,
} from "@material-ui/core";
import { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { InfoBox } from "componentsNew/elements/InfoBox";
import { LogoColorPng } from "icon/LogoPng";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { SCHEDULE_TABLE_COLUMNS } from "pages/Schedule/scheduleTable";
import { ScheduleHeadCellStats } from "pages/Schedule/ScheduleHeadCell";
import {
  getFilteredScheduleIds,
  getScheduleColumnDate,
  getScheduleFooterValue,
} from "utils/scheduleHelpers";
import { ScheudlePdfRenderRow } from "./ScheudlePdfRenderRow";
import { usePdfScheduleStyles } from "./styles";

export const SchedulePdfRenderTable = () => {
  const classes = usePdfScheduleStyles();
  const { userData } = useTypeSelectors((state) => state.auth);
  const { scheduleFilters, userList, dataList, dateList, planList } = useTypeSelectors(
    (state) => state.scheduleTable,
  );

  const rowIds = useTypeSelectors(
    (state) =>
      getFilteredScheduleIds(
        state.scheduleTable.userList,
        state.scheduleTable.scheduleFilters?.userRoles,
        state.scheduleTable.scheduleFilters?.shiftRoles,
        state.scheduleTable.scheduleFilters.search,
        state.scheduleTable.scheduleFilters.ordering || "-id",
        state.scheduleTable.dataList,
        state.scheduleTable.scheduleFilters.station,
      ),
    shallowEqual,
  );

  const tableColumns = useMemo(
    () =>
      SCHEDULE_TABLE_COLUMNS.filter((col) => col.id !== "checkbox" && !col.id.includes("-2")).map(
        (col) => ({
          ...col,
          date: getScheduleColumnDate(dateList, col.label),
        }),
      ),
    [dateList],
  );

  const footerUsers = useMemo(
    () =>
      rowIds
        .map((id) => userList.find((user) => user.id === id))
        .filter((user): user is NonNullable<typeof user> => Boolean(user)),
    [rowIds, userList],
  );

  const stationTitle =
    userData.station_spots.find((spot) => spot.station.id === Number(scheduleFilters?.station))
      ?.station.title || "None";

  return (
    <Box id="tablePdf">
      <Box className={classes.mainHead}>
        <Box className={classes.logoBox}>
          <LogoColorPng width={150} height={30} />
        </Box>
        <Box className={classes.logoBox}>
          <InfoBox label={"Week"} value={scheduleFilters?.week_date} horizontal />
          <InfoBox label={"Station"} value={stationTitle} horizontal />
        </Box>
      </Box>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {tableColumns.map((col, index) => (
              <TableCell
                key={col.id}
                className={`${!col.dateCol ? classes.tableHeadCell : classes.tableHeadDateCell} ${
                  index > 0 ? classes.tableBorderLeft : ""
                }`}
              >
                {col.dateCol ? (
                  <Box className={classes.dateHeadContent}>
                    <Box className={classes.dateHeadDay}>
                      <Typography className={classes.dateHeadDayLabel}>
                        {col.label.replace(" 2", "").slice(0, 3)}
                        {col.date ? `, ${col.date.slice(8, 10)}-${col.date.slice(5, 7)}` : ""}
                      </Typography>
                    </Box>
                    <Box className={classes.dateHeadFooter}>
                      <ScheduleHeadCellStats
                        date={col.date}
                        station={scheduleFilters.station}
                        dataList={dataList}
                        planList={planList}
                        classes={classes}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography className={classes.dateHeadDayLabel}>{col.label}</Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {footerUsers.map((user) => (
            <ScheudlePdfRenderRow key={user.id} user={user} columns={tableColumns} classes={classes} />
          ))}
        </TableBody>
        <TableFooter className={classes.tableFooter}>
          <TableRow>
            {tableColumns.map((col, index) => (
              <TableCell
                key={`footer-${col.id}`}
                className={`${classes.tableFooterCell} ${index > 0 ? classes.tableBorderLeft : ""}`}
              >
                <Box className={classes.footerBox}>
                  {col.id === "user" && <PersonOutlinedIcon className={classes.footerIcon} />}
                  {(col.id === "hours" || col.dateCol) && (
                    <AccessTimeOutlinedIcon className={classes.footerIcon} />
                  )}
                  <Typography className={classes.footerText}>
                    {getScheduleFooterValue(col.id, scheduleFilters.station, col.date, footerUsers, dataList)}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
    </Box>
  );
};
